import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useOutletContext } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import { DOCUMENT } from '../../constants/documentTheme';
import { developerPath, publicApiBase } from '../../utils/developerHost';
import { planNameKey } from '../../utils/partnerPlan';
import { canAccessDeveloperApiTools } from '../../utils/partnerTierGates';
import DeveloperAccessCallout from '../../components/DeveloperAccessCallout';

const cardSx = {
  bgcolor: DOCUMENT.surface,
  border: `1px solid ${DOCUMENT.ruleHard}`,
  borderRadius: 0,
  boxShadow: 'none',
  p: { xs: 2.5, md: 3 },
  height: '100%',
};

const DeveloperHome = () => {
  const { t } = useTranslation();
  const {
    entitlements: layoutEntitlements,
    setEntitlements,
    refreshEntitlements,
    canUseApiTools: layoutCanUseApiTools,
  } = useOutletContext() || {};

  const [plan, setPlan] = useState(layoutEntitlements || null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(!layoutEntitlements);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [planRes, usageRes] = await Promise.all([
        api.get('/partners/plan', { usePartnerAuth: true }),
        api.get('/partners/developer/usage', { usePartnerAuth: true }).catch(() => null),
      ]);
      const entitlements = planRes.data?.entitlements || null;
      setPlan(entitlements);
      setEntitlements?.(entitlements);
      setUsage(usageRes?.data || null);
    } catch (err) {
      setError(err.response?.data?.error || t('developerPortal.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t, setEntitlements]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (layoutEntitlements) {
      setPlan(layoutEntitlements);
    }
  }, [layoutEntitlements]);

  const features = plan?.features || {};
  const hasSandbox = Boolean(features.hasApiSandbox);
  const hasProduction = Boolean(features.hasProductionApi);
  const canUseApiTools = layoutCanUseApiTools ?? canAccessDeveloperApiTools(plan);
  const planId = plan?.tier || plan?.id;
  const planLabel = planId
    ? t(planNameKey(planId), { defaultValue: plan?.planName || planId })
    : t('developerPortal.home.unknownPlan');

  const statusCopy = hasProduction
    ? t('developerPortal.home.hasProduction')
    : hasSandbox
      ? t('developerPortal.home.hasSandbox')
      : t('developerPortal.home.noApi');

  const sandboxUsed = usage?.sandbox?.used;
  const liveUsed = usage?.production?.used;
  const usageLabel = (sandboxUsed != null || liveUsed != null)
    ? t('developerPortal.home.requestsSplit', {
      sandbox: sandboxUsed || 0,
      production: liveUsed || 0,
    })
    : t('developerPortal.home.usageUnavailable');

  const links = [
    { to: 'sandbox', label: t('developerPortal.nav.sandbox'), blurb: t('developerPortal.home.links.sandbox') },
    { to: 'docs/api', label: t('developerPortal.nav.apiReference'), blurb: t('developerPortal.home.links.apiRef') },
    { to: 'widgets', label: t('developerPortal.nav.widgets'), blurb: t('developerPortal.home.links.widgets') },
    { to: 'docs', label: t('developerPortal.nav.docs'), blurb: t('developerPortal.home.links.docs') },
    { to: 'keys', label: t('developerPortal.nav.keys'), blurb: t('developerPortal.home.links.keys'), requiresApi: true },
    { to: 'webhooks', label: t('developerPortal.nav.webhooks'), blurb: t('developerPortal.home.links.webhooks'), requiresApi: true },
    { to: 'usage', label: t('developerPortal.nav.usage'), blurb: t('developerPortal.home.links.usage'), requiresApi: true },
  ];

  if (loading && !plan) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: DOCUMENT.oxblood }} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        component="h1"
        sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 28, md: 36 }, color: DOCUMENT.ink, mb: 1 }}
      >
        {t('developerPortal.home.title')}
      </Typography>
      <Typography sx={{ color: DOCUMENT.ink2, mb: 3, maxWidth: '62ch' }}>
        {t('developerPortal.home.subtitle')}
      </Typography>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <DeveloperAccessCallout
        entitlements={plan}
        onEntitlementsChange={async () => {
          await refreshEntitlements?.();
          await load();
        }}
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={cardSx}>
            <Typography sx={{ fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: DOCUMENT.ink3, mb: 1 }}>
              {t('developerPortal.home.currentPlan')}
            </Typography>
            <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22, mb: 1 }}>{planLabel}</Typography>
            <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14 }}>{statusCopy}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ ...cardSx, opacity: canUseApiTools ? 1 : 0.72 }}>
            <Typography sx={{ fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: DOCUMENT.ink3, mb: 1 }}>
              {t('developerPortal.home.usageLabel')}
            </Typography>
            <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22, mb: 1 }}>
              {canUseApiTools ? usageLabel : t('developerPortal.home.usageLocked')}
            </Typography>
            <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14 }}>
              {t('developerPortal.home.apiBase', { url: publicApiBase() })}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {links.map((link) => {
          const locked = Boolean(link.requiresApi) && !canUseApiTools;
          return (
            <Grid item xs={12} sm={6} key={link.to}>
              <Paper elevation={0} sx={{ ...cardSx, opacity: locked ? 0.72 : 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                  <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 20 }}>
                    {link.label}
                  </Typography>
                  {locked ? (
                    <LockOutlinedIcon sx={{ fontSize: 16, color: DOCUMENT.ink3 }} />
                  ) : null}
                </Box>
                <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14, mb: 2 }}>
                  {locked ? t('developerPortal.home.lockedBlurb') : link.blurb}
                </Typography>
                <Button
                  component={locked ? 'button' : RouterLink}
                  to={locked ? undefined : developerPath(link.to)}
                  variant="outlined"
                  disabled={locked}
                  sx={{
                    borderRadius: 0,
                    borderColor: DOCUMENT.ruleHard,
                    color: DOCUMENT.ink,
                    textTransform: 'none',
                    '&.Mui-disabled': {
                      borderColor: DOCUMENT.rule,
                      color: DOCUMENT.ink3,
                    },
                  }}
                >
                  {locked ? t('developerPortal.home.lockedCta') : t('developerPortal.home.open')}
                </Button>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default DeveloperHome;
